import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useShop from "@saleor/hooks/useShop";
import { sectionNames } from "@saleor/intl";
import useBrandUpdate from "@saleor/products/hooks/useBrandUpdate";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import { changeTimeZone } from "@saleor/utils/smop/changeTimeZoneDateTime";
import React from "react";
import { useIntl } from "react-intl";

import { decimal, maybe } from "../../misc";
import { DiscountValueTypeEnum, SaleType } from "../../types/globalTypes";
import SaleCreatePage from "../components/SaleCreatePage";
import useSaleUserType from "../hooks/useSaleUserType";
import { TypedSaleCreate } from "../mutations";
import { SaleCreate } from "../types/SaleCreate";
import { saleListUrl } from "../urls";
function discountValueTypeEnum(type: SaleType): DiscountValueTypeEnum {
  switch (type.toString()) {
    case DiscountValueTypeEnum.FIXED:
      return DiscountValueTypeEnum.FIXED;
    case DiscountValueTypeEnum.PERCENTAGE:
      return DiscountValueTypeEnum.PERCENTAGE;
    case DiscountValueTypeEnum.ABSOLUTE_VALUE:
      return DiscountValueTypeEnum.ABSOLUTE_VALUE;
  }
}

interface SaleCreateProps {
  params?: {
    brand: string;
  };
}

export const SaleDetails: React.FC<SaleCreateProps> = ({ params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const shop = useShop();
  const intl = useIntl();

  const saleUser = useSaleUserType();

  const { result: brandsResult } = useBrandPrivateSearch({
    skip: !saleUser.isKAMS,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      accountManager: saleUser.id
    }
  });

  const brands = maybe(() => brandsResult?.data?.search?.edges, []).map(
    edge => edge.node
  );

  // Hook for updating of brands
  const [onBrandChange, brand, brandChoices] = useBrandUpdate(
    params,
    brands,
    brandsResult.loading
  );

  const handleSaleCreate = (data: SaleCreate) => {
    if (data.saleCreate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Sales Created"
        })
      });
      // navigate(saleUrl(data.saleCreate.sale.id), true);
      navigate(saleListUrl());
    } else if (
      data.saleCreate.errors.some(e => e.code === "UNIQUE") &&
      data.saleCreate.errors.some(e => e.field === "name")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Discount name is already existing"
        })
      });
    }
  };

  return (
    <TypedSaleCreate onCompleted={handleSaleCreate}>
      {(saleCreate, saleCreateOpts) => (
        <>
          <WindowTitle title={intl.formatMessage(sectionNames.sales)} />
          <SaleCreatePage
            isKAMS={saleUser.isKAMS}
            brand={brand.value}
            kamsBrands={brandChoices}
            brandLabel={brand.label}
            onBrandChange={onBrandChange}
            defaultCurrency={maybe(() => shop.defaultCurrency)}
            disabled={saleCreateOpts.loading}
            errors={saleCreateOpts.data?.saleCreate.errors || []}
            onBack={() => navigate(saleListUrl())}
            onSubmit={formData =>
              saleCreate({
                variables: {
                  input: {
                    description: formData.description,
                    endDate: changeTimeZone(formData.endDate, formData.endTime),
                    isAppliedToAllMalls: false,
                    isAppliedToAllStores: true,
                    name: formData.name,
                    startDate: changeTimeZone(
                      formData.startDate,
                      formData.startTime
                    ),
                    type: discountValueTypeEnum(formData.type),
                    value: decimal(formData.value),
                    ...(saleUser.isKAMS && {
                      brand: formData.brand
                    })
                  }
                }
              })
            }
            saveButtonBarState={saleCreateOpts.status}
          />
        </>
      )}
    </TypedSaleCreate>
  );
};
export default SaleDetails;
